<template>
  <div class="h-100">
    <div class="d-flex align-items-center mb-2">
      <chip-selector :items="['All', 'Related', 'Unrelated']" v-model="filter" class="mr-auto"/>
      <b-btn size="sm" variant="link" class="py-0" @click="expandAll = !expandAll">
        {{ expandAll ? 'Collapse older' : 'Expand all' }}
      </b-btn>
    </div>

    <!--
      The thread as a conversation: newest at the top, each message a card on
      the sent or received side. An email shows only what was new in it - the
      quoted chain every mail client appends is folded under a link, and a long
      body is cut with a "show more". Older messages start collapsed to a line.
      eBay messages carry no chain and are shown whole, as before.
    -->
    <b-card v-for="(message, i) in rendered" :key="message.id" no-body
            class="mb-3 history-card" :class="message.is_sent_message ? 'ml-5 sent' : 'mr-5'">
      <div class="px-3 py-2 d-flex align-items-center cursor-pointer header" @click="toggle(message)">
        <font-awesome-icon :icon="message.is_sent_message ? 'fa-solid fa-arrow-turn-up' : 'fa-solid fa-arrow-turn-down'"
                           class="mr-2" :class="message.is_sent_message ? 'text-primary' : 'text-secondary'" size="sm"/>
        <div class="mr-auto text-truncate">
          <span class="font-weight-bold">
            {{ message.is_sent_message ? (message.send_by_user ? message.send_by_user.name : 'Sent') : (message.address_display || message.account_address) }}
          </span>
          <span class="text-muted small ml-2" v-if="message.is_sent_message">to {{ message.account_address }}</span>
          <span class="small text-muted ml-2 d-none d-md-inline" v-if="isOpen(message, i) && message.subject">&middot; {{ message.subject }}</span>
          <span class="small text-muted ml-2 preview" v-if="!isOpen(message, i)">&middot; {{ preview(message) }}</span>
        </div>
        <div class="ml-3 text-nowrap small text-muted" :title="message.created | date">{{ message.created | timediff }}</div>
        <b-badge class="ml-2" variant="info" v-if="message.files" pill :title="message.files + ' attachment(s)'">
          <font-awesome-icon icon="fa-solid fa-paperclip"/>
        </b-badge>
        <b-badge class="ml-2" variant="warning" v-if="message.state == 'f'" pill>draft</b-badge>
        <b-badge class="ml-2" variant="light" v-if="!message.same_ref" pill title="Not about this record">unrelated</b-badge>
        <b-btn size="sm" variant="link" class="ml-1 py-0 px-1 text-muted" :title="'Open ' + message.gli" @click.stop="$emit('click', message)">
          <font-awesome-icon icon="fa-solid fa-up-right-from-square" size="xs"/>
        </b-btn>
      </div>

      <div class="px-3 pb-2" v-if="isOpen(message, i)">
        <!-- eBay: whole, as before -->
        <div v-if="!message.text" class="text-pre-line text-body-2 body" v-html="message.content"/>

        <template v-else>
          <div class="text-pre-line text-body-2 body" :class="{'clamped': needsClamp(message) && !isLong(message)}">{{ message.text.text }}</div>
          <div v-if="needsClamp(message) && !isLong(message)" class="fade-out"/>
          <b-btn v-if="needsClamp(message)" size="sm" variant="link" class="px-0 py-0" @click="toggleLong(message)">
            {{ isLong(message) ? 'Show less' : 'Show more' }}
          </b-btn>

          <div v-if="message.text.quoted" class="mt-1">
            <b-btn size="sm" variant="link" class="px-0 py-0 text-muted" @click="toggleQuoted(message)">
              <font-awesome-icon :icon="isQuotedOpen(message) ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'" class="mr-1"/>
              {{ isQuotedOpen(message) ? 'Hide' : 'Show' }} quoted history ({{ message.text.quoted_lines }} lines)
            </b-btn>
            <div v-if="isQuotedOpen(message)" class="text-pre-line small text-muted border-left pl-2 mt-1 quoted">{{ message.text.quoted }}</div>
          </div>
        </template>
      </div>
    </b-card>

    <div v-if="rendered && !rendered.length" class="small text-muted">No messages.</div>
  </div>
</template>

<script>
const OPEN_BY_DEFAULT = 3       // newest messages open; older ones fold to a line
const CLAMP_CHARS = 700         // a body longer than this is cut behind "show more"

export default {
  name: "message-history",
  props: {
    url: String,
    shown: Boolean,
  },
  data() {
    return {
      list: null,
      filter: 'All',
      expandAll: false,
      opened: {},     // id -> true/false, once the user has toggled a card
      long: {},       // id -> body fully shown
      quoted: {},     // id -> quoted chain shown
    }
  },
  computed: {
    rendered() {
      return this.list && this.list.filter(f => {
        if (this.filter == 'Related') return f.same_ref
        if (this.filter == 'Unrelated') return !f.same_ref
        return true
      })
    },
  },
  methods: {
    async refresh() {
      this.list = await this.asyncAction(this.url + '/history', 'GET')
    },

    isOpen(m, i) {
      if (m.id in this.opened) return this.opened[m.id]
      return this.expandAll || i < OPEN_BY_DEFAULT
    },
    toggle(m) {
      const i = this.rendered.indexOf(m)
      this.$set(this.opened, m.id, !this.isOpen(m, i))
    },

    // one line of what the message said, for a folded card
    preview(m) {
      const t = m.text ? m.text.text : String(m.content || '').replace(/<[^>]+>/g, ' ')
      return t.replace(/\s+/g, ' ').trim().slice(0, 110)
    },

    needsClamp(m) {
      return !!m.text && m.text.text.length > CLAMP_CHARS
    },
    isLong(m) {
      return !!this.long[m.id]
    },
    toggleLong(m) {
      this.$set(this.long, m.id, !this.long[m.id])
    },
    isQuotedOpen(m) {
      return !!this.quoted[m.id]
    },
    toggleQuoted(m) {
      this.$set(this.quoted, m.id, !this.quoted[m.id])
    },
  },
  created() {
    this.refresh()
  }
}
</script>

<style scoped>
.history-card { border-radius: .5rem; }
.history-card.sent { background: #f3f7ff; }
.header { border-bottom: 1px solid rgba(0, 0, 0, .06); }
.preview { font-weight: normal; }
.body { line-height: 1.45; position: relative; }
.body.clamped { max-height: 14em; overflow: hidden; }
.fade-out { height: 2em; margin-top: -2em; position: relative;
  background: linear-gradient(rgba(255, 255, 255, 0), #fff); }
.sent .fade-out { background: linear-gradient(rgba(243, 247, 255, 0), #f3f7ff); }
.quoted { max-height: 24em; overflow-y: auto; }
.cursor-pointer { cursor: pointer; }
</style>
